import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Drawer,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Select,
    Space,
    Table,
    Typography,
} from "antd";
import { useParams } from "react-router-dom";
import { SectionPanelProps } from "./SectionTypes";
import {
    BOOKING_INTERVAL_MAX_MINS,
    BOOKING_INTERVAL_MIN_MINS,
    BOOKING_INTERVAL_PRESETS,
    BOOKING_INTERVAL_STEP_MINS,
    FacilityMetadataEntry,
    FacilityRow,
    isAllowedBookingInterval,
} from "../../../model/facilities/FacilityRow";
import { CreateFacilityRequest } from "../../../requests/facility/CreateFacilityRequest";
import { UpdateFacilityRequest } from "../../../requests/facility/UpdateFacilityRequest";
import { useClientConfigService } from "../../../services/clients/ClientConfigServiceProvider";
import { Switch } from "../../../ui/primitives/Switch";

const { Text } = Typography;

interface FacilityFormValues {
    name: string;
    description: string;
    facilityTypeId: number | null;
    bookingIntervalMins: number;
    timezone: string;
    // FORT-401. Independent flags: isActive gates booking, isVisible only decides
    // whether the facility's column renders on the admin booking grid.
    isActive: boolean;
    isVisible: boolean;
}

// Sentinel for the "Custom…" Select option. Picked as -1 because it sits
// outside the valid booking-interval range (>= 5), so it can never collide
// with a real value the server would accept. When picked, we render an
// adjacent InputNumber for the operator to type any allowed value.
const CUSTOM_INTERVAL_SENTINEL = -1;

const EMPTY_FORM: FacilityFormValues = {
    name: '',
    description: '',
    facilityTypeId: null,
    bookingIntervalMins: 30,
    // Defaulting to UTC (matches the DB default) keeps the create form valid
    // without leaking the operator's local tz, which is rarely what a club
    // wants stored against the facility.
    timezone: 'UTC',
    // Matches the column defaults — a facility you just created is in service.
    isActive: true,
    isVisible: true,
};

const toFormValues = (row: FacilityRow): FacilityFormValues => ({
    name: row.name,
    description: row.description ?? '',
    facilityTypeId: row.facilityTypeId,
    bookingIntervalMins: row.bookingIntervalMins,
    timezone: row.timezone,
    // A missing flag reads as in-service, matching adaptBookingsToSchedules. Amplify
    // auto-deploys the frontend while the API ships via CircleCI, so there is a real
    // window where this screen is newer than the API and the fields are absent — and
    // `checked={undefined}` would render every court as off AND uncontrolled, then
    // persist that on the first click. [FORT-401]
    isActive: row.isActive !== false,
    isVisible: row.isVisible !== false,
});

const toRequest = (values: FacilityFormValues): CreateFacilityRequest => ({
    name: values.name.trim(),
    description: values.description.trim() === '' ? null : values.description.trim(),
    facilityTypeId: values.facilityTypeId ?? 0,
    bookingIntervalMins: values.bookingIntervalMins,
    timezone: values.timezone.trim(),
});

// The PUT replaces the whole row, so it must carry the flags too — sending only
// the create shape would silently reset a disabled facility back to in-service on
// the next unrelated edit. [FORT-401]
const toUpdateRequest = (values: FacilityFormValues): UpdateFacilityRequest => ({
    ...toRequest(values),
    isActive: values.isActive,
    isVisible: values.isVisible,
});

// Facility settings panel — table + drawer-based edit + inline metadata
// editor. Ignores value/onChange because it manages its own row list rather
// than bubbling state up to the shell, same pattern as
// MemberCategoriesPanel (FORT-155).
export const FacilitiesPanel: React.FC<SectionPanelProps<unknown>> = () => {
    const service = useClientConfigService();
    const { clientId: clientIdParam } = useParams<{ clientId: string }>();
    const clientId = clientIdParam !== undefined ? Number(clientIdParam) : NaN;

    const [rows, setRows] = useState<FacilityRow[] | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);

    const [editingId, setEditingId] = useState<number | 'new' | null>(null);
    const [draft, setDraft] = useState<FacilityFormValues>(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    // Whether the operator has explicitly picked "Custom…" from the interval
    // Select. Tracked separately from `draft.bookingIntervalMins` so toggling
    // between preset and custom modes never mutates the underlying saved
    // value — the operator must actively type a new number for the form to
    // change. Initialised from whatever the current draft holds: if the
    // value isn't one of the presets we start in custom mode, otherwise the
    // preset Select shows the matching option.
    const [customIntervalMode, setCustomIntervalMode] = useState<boolean>(
        () => !BOOKING_INTERVAL_PRESETS.some(opt => opt.value === EMPTY_FORM.bookingIntervalMins),
    );

    // Ids whose inline Active/Visible switch is mid-flight, so the toggle can show
    // a spinner and refuse a second click while the PUT is in the air. [FORT-401]
    const [togglingIds, setTogglingIds] = useState<ReadonlySet<number>>(new Set());

    // Metadata drawer state — separate from the edit modal so an operator can
    // tweak k/v rows without re-saving the whole facility form.
    const [metadataFacility, setMetadataFacility] = useState<FacilityRow | null>(null);
    const [metadataEntries, setMetadataEntries] = useState<FacilityMetadataEntry[]>([]);
    const [metadataLoading, setMetadataLoading] = useState(false);
    const [metadataSaving, setMetadataSaving] = useState(false);
    const [metadataError, setMetadataError] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        if (!Number.isFinite(clientId)) return;
        try {
            const next = await service.listFacilities(clientId);
            setRows(next);
            setLoadError(null);
        } catch (err) {
            setLoadError(err instanceof Error ? err.message : 'Failed to load facilities.');
        }
    }, [service, clientId]);

    useEffect(() => {
        if (!Number.isFinite(clientId)) {
            setLoadError('Invalid client id in URL.');
            return;
        }
        setLoadError(null);
        void refresh();
    }, [clientId, refresh]);

    const handleOpenCreate = () => {
        setDraft(EMPTY_FORM);
        setCustomIntervalMode(
            !BOOKING_INTERVAL_PRESETS.some(opt => opt.value === EMPTY_FORM.bookingIntervalMins),
        );
        setEditingId('new');
        setActionError(null);
    };

    const handleOpenEdit = (row: FacilityRow) => {
        const next = toFormValues(row);
        setDraft(next);
        setCustomIntervalMode(
            !BOOKING_INTERVAL_PRESETS.some(opt => opt.value === next.bookingIntervalMins),
        );
        setEditingId(row.id);
        setActionError(null);
    };

    const handleCloseModal = () => {
        if (saving) return;
        setEditingId(null);
        setActionError(null);
    };

    const isFormValid = useMemo(() => {
        return draft.name.trim() !== ''
            && draft.facilityTypeId != null
            && draft.facilityTypeId > 0
            && draft.timezone.trim() !== ''
            && isAllowedBookingInterval(draft.bookingIntervalMins);
    }, [draft]);

    const handleSave = async () => {
        if (editingId === null) return;
        if (!isFormValid) {
            setActionError('Name, facility type, timezone and a valid booking interval are required.');
            return;
        }
        setSaving(true);
        setActionError(null);
        try {
            if (editingId === 'new') {
                await service.createFacility(clientId, toRequest(draft));
            } else {
                await service.updateFacility(clientId, editingId, toUpdateRequest(draft));
            }
            setEditingId(null);
            await refresh();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to save facility.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = useCallback(async (row: FacilityRow) => {
        // Delete is now refused while upcoming bookings remain (FORT-401), so a failed
        // delete is an expected path — without this, the stale "3 upcoming booking(s)"
        // alert survives the retry that succeeds.
        setActionError(null);
        try {
            await service.deleteFacility(clientId, row.id);
            await refresh();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to delete facility.');
        }
    }, [service, clientId, refresh]);

    // Inline switch on the table row. The API's PUT replaces the whole facility, so
    // the payload is rebuilt from the row we already hold with just the one flag
    // flipped — the other flag is sent unchanged, which is what keeps the two
    // independent (FORT-401) instead of one toggle clobbering the other.
    const handleToggleFlag = useCallback(async (
        row: FacilityRow,
        key: 'isActive' | 'isVisible',
        next: boolean,
    ) => {
        setActionError(null);
        setTogglingIds(prev => new Set(prev).add(row.id));
        // Optimistic: the switch tracks the row list, so flip it locally first and
        // let the refresh below confirm. On failure we roll back explicitly rather
        // than leaving the UI claiming a state the server rejected.
        setRows(prev => prev?.map(r => r.id === row.id ? { ...r, [key]: next } : r) ?? prev);
        try {
            await service.updateFacility(clientId, row.id, {
                name: row.name,
                description: row.description,
                facilityTypeId: row.facilityTypeId,
                bookingIntervalMins: row.bookingIntervalMins,
                timezone: row.timezone,
                isActive: key === 'isActive' ? next : row.isActive !== false,
                isVisible: key === 'isVisible' ? next : row.isVisible !== false,
            });
            await refresh();
        } catch (err) {
            setRows(prev => prev?.map(r => r.id === row.id ? { ...r, [key]: !next } : r) ?? prev);
            setActionError(err instanceof Error ? err.message : 'Failed to update facility.');
        } finally {
            setTogglingIds(prev => {
                const nextSet = new Set(prev);
                nextSet.delete(row.id);
                return nextSet;
            });
        }
    }, [service, clientId, refresh]);

    const updateDraft = <K extends keyof FacilityFormValues>(key: K, value: FacilityFormValues[K]) => {
        setDraft(prev => ({ ...prev, [key]: value }));
    };

    // ----- metadata handlers -----
    const handleOpenMetadata = useCallback(async (row: FacilityRow) => {
        setMetadataFacility(row);
        setMetadataError(null);
        setMetadataLoading(true);
        try {
            const entries = await service.listFacilityMetadata(clientId, row.id);
            setMetadataEntries(entries);
        } catch (err) {
            setMetadataError(err instanceof Error ? err.message : 'Failed to load metadata.');
            setMetadataEntries([]);
        } finally {
            setMetadataLoading(false);
        }
    }, [service, clientId]);

    const handleCloseMetadata = () => {
        if (metadataSaving) return;
        setMetadataFacility(null);
        setMetadataEntries([]);
        setMetadataError(null);
    };

    const handleAddMetadataRow = () => {
        setMetadataEntries(prev => [...prev, { name: '', value: '' }]);
    };

    const handleRemoveMetadataRow = (index: number) => {
        setMetadataEntries(prev => prev.filter((_, i) => i !== index));
    };

    const handleMetadataChange = (index: number, key: 'name' | 'value', next: string) => {
        setMetadataEntries(prev => prev.map((entry, i) => i === index ? { ...entry, [key]: next } : entry));
    };

    // Duplicate-key detection client-side so the operator gets feedback before
    // round-tripping to the server's 400.
    const metadataDuplicateKey = useMemo(() => {
        const names = metadataEntries.map(e => e.name.trim()).filter(n => n.length > 0);
        return new Set(names).size !== names.length;
    }, [metadataEntries]);

    const metadataHasEmptyKey = useMemo(
        () => metadataEntries.some(e => e.name.trim() === ''),
        [metadataEntries]
    );

    const handleSaveMetadata = async () => {
        if (metadataFacility === null) return;
        if (metadataDuplicateKey) {
            setMetadataError('Duplicate keys are not allowed.');
            return;
        }
        if (metadataHasEmptyKey) {
            setMetadataError('All entries must have a non-empty key.');
            return;
        }
        setMetadataSaving(true);
        setMetadataError(null);
        try {
            const cleaned = metadataEntries.map(e => ({ name: e.name.trim(), value: e.value }));
            await service.replaceFacilityMetadata(clientId, metadataFacility.id, { entries: cleaned });
            // Refresh from server so the canonical order matches the list query.
            const next = await service.listFacilityMetadata(clientId, metadataFacility.id);
            setMetadataEntries(next);
        } catch (err) {
            setMetadataError(err instanceof Error ? err.message : 'Failed to save metadata.');
        } finally {
            setMetadataSaving(false);
        }
    };

    const columns = useMemo(() => [
        {
            title: 'Name',
            dataIndex: 'name' as const,
            render: (name: string, row: FacilityRow) => (
                <span data-testid={`fac-row-name-${row.id}`}>{name}</span>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description' as const,
            render: (description: string | null) => description ?? <Text type="secondary">—</Text>,
        },
        {
            title: 'Type',
            dataIndex: 'facilityTypeId' as const,
            render: (typeId: number) => typeId,
        },
        {
            title: 'Interval',
            dataIndex: 'bookingIntervalMins' as const,
            render: (mins: number) => `${mins} min`,
        },
        {
            title: 'Timezone',
            dataIndex: 'timezone' as const,
        },
        {
            title: 'Active',
            key: 'isActive',
            render: (_: unknown, row: FacilityRow) => (
                <Switch
                    data-testid={`fac-active-${row.id}`}
                    aria-label={`${row.name} active`}
                    checked={row.isActive !== false}
                    loading={togglingIds.has(row.id)}
                    disabled={togglingIds.has(row.id)}
                    onChange={(next) => void handleToggleFlag(row, 'isActive', next)}
                />
            ),
        },
        {
            title: 'Visible',
            key: 'isVisible',
            render: (_: unknown, row: FacilityRow) => (
                <Switch
                    data-testid={`fac-visible-${row.id}`}
                    aria-label={`${row.name} visible`}
                    checked={row.isVisible !== false}
                    loading={togglingIds.has(row.id)}
                    disabled={togglingIds.has(row.id)}
                    onChange={(next) => void handleToggleFlag(row, 'isVisible', next)}
                />
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: unknown, row: FacilityRow) => (
                <Space>
                    <Button
                        size="small"
                        data-testid={`fac-edit-${row.id}`}
                        onClick={() => handleOpenEdit(row)}
                    >
                        Edit
                    </Button>
                    <Button
                        size="small"
                        data-testid={`fac-metadata-${row.id}`}
                        onClick={() => void handleOpenMetadata(row)}
                    >
                        Metadata
                    </Button>
                    <Popconfirm
                        title="Delete this facility?"
                        // Delete is refused outright while upcoming bookings remain
                        // (FORT-401), so say that rather than the old "it will be
                        // hidden instead" — which is now only true for a facility
                        // whose bookings are all in the past.
                        description="If it has upcoming bookings, they must be cancelled or moved first. To close it temporarily, switch Active off instead."
                        okText="Delete"
                        cancelText="Cancel"
                        onConfirm={() => handleDelete(row)}
                    >
                        <Button size="small" danger data-testid={`fac-delete-${row.id}`}>Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ], [handleDelete, handleOpenMetadata, handleToggleFlag, togglingIds]);

    if (loadError) {
        return <Alert type="error" message={loadError} data-testid="fac-load-error" />;
    }
    if (rows === null) {
        return <Text type="secondary">Loading…</Text>;
    }

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
                <Button
                    type="primary"
                    data-testid="fac-add-btn"
                    onClick={handleOpenCreate}
                >
                    Add facility
                </Button>
            </Space>
            {actionError && (
                <Alert type="error" message={actionError} data-testid="fac-action-error" />
            )}
            <Table
                rowKey="id"
                dataSource={rows}
                columns={columns}
                pagination={false}
                data-testid="fac-table"
                locale={{ emptyText: 'No facilities yet.' }}
            />
            <Modal
                open={editingId !== null}
                title={editingId === 'new' ? 'Add facility' : 'Edit facility'}
                onCancel={handleCloseModal}
                onOk={handleSave}
                okText="Save"
                confirmLoading={saving}
                okButtonProps={{
                    disabled: !isFormValid,
                    'data-testid': 'fac-modal-save',
                } as any}
                cancelButtonProps={{ disabled: saving }}
                destroyOnClose
            >
                <Form layout="vertical" data-testid="fac-form">
                    <Form.Item
                        label="Name"
                        validateStatus={draft.name.trim() === '' ? 'error' : undefined}
                        help={draft.name.trim() === '' ? 'Name is required.' : undefined}
                    >
                        <Input
                            data-testid="fac-form-name"
                            value={draft.name}
                            onChange={(e) => updateDraft('name', e.target.value)}
                            autoFocus
                            maxLength={100}
                        />
                    </Form.Item>
                    <Form.Item label="Description">
                        <Input.TextArea
                            data-testid="fac-form-description"
                            value={draft.description}
                            onChange={(e) => updateDraft('description', e.target.value)}
                            rows={2}
                            maxLength={2000}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Facility type id"
                        help="Numeric id of a facility type owned by this client."
                        validateStatus={
                            draft.facilityTypeId == null || draft.facilityTypeId <= 0 ? 'error' : undefined
                        }
                    >
                        <Input
                            data-testid="fac-form-facility-type-id"
                            type="number"
                            inputMode="numeric"
                            min={1}
                            value={draft.facilityTypeId ?? ''}
                            onChange={(e) => {
                                const next = e.target.value === '' ? null : Number(e.target.value);
                                updateDraft('facilityTypeId', Number.isFinite(next as number) ? next : null);
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Booking interval"
                        help={`Multiples of ${BOOKING_INTERVAL_STEP_MINS} minutes, between ${BOOKING_INTERVAL_MIN_MINS} and ${BOOKING_INTERVAL_MAX_MINS}.`}
                        validateStatus={isAllowedBookingInterval(draft.bookingIntervalMins) ? undefined : 'error'}
                    >
                        <Space.Compact style={{ width: '100%' }}>
                            <Select<number>
                                data-testid="fac-form-interval"
                                style={{ width: '60%' }}
                                value={
                                    customIntervalMode
                                        ? CUSTOM_INTERVAL_SENTINEL
                                        : draft.bookingIntervalMins
                                }
                                onChange={(value) => {
                                    if (value === CUSTOM_INTERVAL_SENTINEL) {
                                        // Pure UI toggle — do NOT touch draft.bookingIntervalMins.
                                        // The current value stays as-is; the operator must
                                        // actively type a new one in the InputNumber for the
                                        // form state to change.
                                        setCustomIntervalMode(true);
                                    } else {
                                        setCustomIntervalMode(false);
                                        updateDraft('bookingIntervalMins', value);
                                    }
                                }}
                                options={[
                                    ...BOOKING_INTERVAL_PRESETS,
                                    { value: CUSTOM_INTERVAL_SENTINEL, label: 'Custom…' },
                                ] as { value: number; label: string }[]}
                            />
                            {customIntervalMode && (
                                <InputNumber
                                    data-testid="fac-form-interval-custom"
                                    inputMode="numeric"
                                    style={{ width: '40%' }}
                                    min={BOOKING_INTERVAL_MIN_MINS}
                                    max={BOOKING_INTERVAL_MAX_MINS}
                                    step={BOOKING_INTERVAL_STEP_MINS}
                                    addonAfter="min"
                                    value={draft.bookingIntervalMins}
                                    onChange={(value) => {
                                        if (typeof value === 'number') {
                                            updateDraft('bookingIntervalMins', value);
                                        }
                                    }}
                                />
                            )}
                        </Space.Compact>
                    </Form.Item>
                    <Form.Item
                        label="Timezone (IANA)"
                        help="e.g. Europe/London, America/New_York. Must be a valid IANA timezone id."
                        validateStatus={draft.timezone.trim() === '' ? 'error' : undefined}
                    >
                        <Input
                            data-testid="fac-form-timezone"
                            value={draft.timezone}
                            onChange={(e) => updateDraft('timezone', e.target.value)}
                            maxLength={100}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Drawer
                open={metadataFacility !== null}
                title={metadataFacility ? `Metadata — ${metadataFacility.name}` : ''}
                width={520}
                onClose={handleCloseMetadata}
                destroyOnClose
                data-testid="fac-metadata-drawer"
                extra={(
                    <Space>
                        <Button onClick={handleCloseMetadata} disabled={metadataSaving}>
                            Close
                        </Button>
                        <Button
                            type="primary"
                            data-testid="fac-metadata-save"
                            onClick={handleSaveMetadata}
                            loading={metadataSaving}
                            disabled={metadataDuplicateKey || metadataHasEmptyKey}
                        >
                            Save
                        </Button>
                    </Space>
                )}
            >
                {metadataLoading && <Text type="secondary">Loading…</Text>}
                {metadataError && (
                    <Alert
                        type="error"
                        message={metadataError}
                        style={{ marginBottom: 12 }}
                        data-testid="fac-metadata-error"
                    />
                )}
                {!metadataLoading && (
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        {metadataEntries.map((entry, index) => (
                            <Space key={index} style={{ width: '100%' }} align="start">
                                <Input
                                    placeholder="key"
                                    value={entry.name}
                                    onChange={(e) => handleMetadataChange(index, 'name', e.target.value)}
                                    maxLength={100}
                                    data-testid={`fac-metadata-key-${index}`}
                                    style={{ width: 180 }}
                                />
                                <Input
                                    placeholder="value"
                                    value={entry.value}
                                    onChange={(e) => handleMetadataChange(index, 'value', e.target.value)}
                                    data-testid={`fac-metadata-value-${index}`}
                                    style={{ width: 220 }}
                                />
                                <Button
                                    danger
                                    onClick={() => handleRemoveMetadataRow(index)}
                                    data-testid={`fac-metadata-remove-${index}`}
                                >
                                    Remove
                                </Button>
                            </Space>
                        ))}
                        <Button
                            onClick={handleAddMetadataRow}
                            data-testid="fac-metadata-add"
                        >
                            Add entry
                        </Button>
                        {metadataDuplicateKey && (
                            <Text type="danger" data-testid="fac-metadata-duplicate">
                                Duplicate keys are not allowed.
                            </Text>
                        )}
                    </Space>
                )}
            </Drawer>
        </Space>
    );
};
